/* =====================================================================
   YMM (Year/Make/Model) data layer
   ---------------------------------------------------------------------
   Phase 1 / 2: reads from the static placeholder JSON in assets/.
   Phase 8 (or sooner): swap getFitment() to call the real fitment DB API.
   The function signature and return shape are the contract — every
   consumer (PDP fitment checker, homepage YMM widget) calls getFitment()
   and works with the same record format.

   Record shape (matches Shopify custom.fitment metafield exactly):
     [{ yearStart, yearEnd, makes: [], models: [] }]

   Helpers built on top of getFitment() expose what UIs actually need:
     - listYears()     → sorted unique years across all records
     - listMakes(year) → makes available for a given year
     - listModels(year, make) → models available for year+make
     - matches(vehicle, ranges) → does a selected vehicle satisfy any range?
   ===================================================================== */

(function () {
  'use strict';

  let _cache = null;
  let _inflight = null;
  let _source = null;

  // ------------------------------------------------------------------
  // configure() — called once with the placeholder URL by the page that
  // mounts the YMM widget. Phase 8 will replace this implementation.
  // ------------------------------------------------------------------
  function configure(opts) {
    _source = opts && opts.source;
  }

  // ------------------------------------------------------------------
  // getFitment() — async, returns array of range records.
  // Cached after first load; safe to call from multiple consumers.
  // ------------------------------------------------------------------
  async function getFitment() {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    if (!_source) {
      console.warn('[ymm] getFitment called before configure() — call window.HHymm.configure({ source: "..." }) first');
      return [];
    }
    _inflight = fetch(_source, { credentials: 'omit' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('YMM source ' + r.status)))
      .then(data => {
        _cache = Array.isArray(data) ? data : [];
        _inflight = null;
        return _cache;
      })
      .catch(err => {
        console.warn('[ymm] failed to load fitment:', err);
        _cache = [];
        _inflight = null;
        return _cache;
      });
    return _inflight;
  }

  // ------------------------------------------------------------------
  // Derived helpers
  // ------------------------------------------------------------------
  async function listYears() {
    const ranges = await getFitment();
    const years = new Set();
    for (const r of ranges) {
      const start = parseInt(r.yearStart, 10);
      const end = parseInt(r.yearEnd, 10);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        for (let y = start; y <= end; y++) years.add(y);
      }
    }
    return Array.from(years).sort((a, b) => b - a); // newest first
  }

  async function listMakes(year) {
    const ranges = await getFitment();
    const makes = new Set();
    const yr = year ? parseInt(year, 10) : null;
    for (const r of ranges) {
      if (yr && (yr < parseInt(r.yearStart, 10) || yr > parseInt(r.yearEnd, 10))) continue;
      for (const m of (r.makes || [])) makes.add(m);
    }
    return Array.from(makes).sort();
  }

  async function listModels(year, make) {
    const ranges = await getFitment();
    const models = new Set();
    const yr = year ? parseInt(year, 10) : null;
    for (const r of ranges) {
      if (yr && (yr < parseInt(r.yearStart, 10) || yr > parseInt(r.yearEnd, 10))) continue;
      if (make && !(r.makes || []).some(m => m.toLowerCase() === make.toLowerCase())) continue;
      for (const m of (r.models || [])) models.add(m);
    }
    return Array.from(models).sort();
  }

  // ------------------------------------------------------------------
  // matches(vehicle, ranges) — pure function, no fetch.
  // Used by the PDP fitment checker against product.metafields.custom.fitment
  // (already inlined into the page; doesn't need to call getFitment()).
  //
  // vehicle: { year, make, model }
  // ranges:  array of { yearStart, yearEnd, makes, models } (e.g., custom.fitment)
  // returns true if vehicle satisfies any range
  // ------------------------------------------------------------------
  function matches(vehicle, ranges) {
    if (!vehicle || !ranges || !ranges.length) return false;
    const yr = parseInt(vehicle.year, 10);
    const mk = (vehicle.make || '').toLowerCase();
    const md = (vehicle.model || '').toLowerCase();
    for (const r of ranges) {
      const start = parseInt(r.yearStart, 10);
      const end = parseInt(r.yearEnd, 10);
      if (!Number.isFinite(yr) || yr < start || yr > end) continue;
      const makeOk = !mk || (r.makes || []).some(m => m.toLowerCase() === mk);
      if (!makeOk) continue;
      const modelOk = !md || !(r.models || []).length || (r.models || []).some(m => m.toLowerCase() === md);
      if (modelOk) return true;
    }
    return false;
  }

  // ------------------------------------------------------------------
  // Vehicle persistence — localStorage per YMM doc
  // ------------------------------------------------------------------
  const STORAGE_KEY = 'hh-ymm-vehicle';

  function getStoredVehicle() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (e) { return null; }
  }

  function setStoredVehicle(v) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('hh:vehicle-changed', { detail: v }));
  }

  function clearStoredVehicle() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    document.dispatchEvent(new CustomEvent('hh:vehicle-changed', { detail: null }));
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------
  window.HHymm = {
    configure: configure,
    getFitment: getFitment,
    listYears: listYears,
    listMakes: listMakes,
    listModels: listModels,
    matches: matches,
    getStoredVehicle: getStoredVehicle,
    setStoredVehicle: setStoredVehicle,
    clearStoredVehicle: clearStoredVehicle,
  };
})();
